import React, { useMemo } from 'react';
import { Alert } from 'antd';

import ItemTagChips from './ItemTagChips';
import useCollectionTags from '../../hooks/useCollectionTags';
import { rememberedItemTagTitles } from '../../hooks/useTagSearch';
import { useOptionalTags } from '../../contexts/TagsContext';
import { itemTagEntries } from '../../utils/itemTags';

/**
 * THE TAGS A TEMPLATE WILL PUT ON — shown before the save (docs/plans/tags.md
 * §5.2, T9: "A template never applies a tag the person can't see land"). A
 * multi add step's tags, as the API judged them for this person where the
 * items are made (`GET /api/tags/usable`): the hand tags and the person's own
 * private tags as chips, each with a ✕ (the person may leave one off), and
 * what the template carried that they can't use, said once.
 *
 * `tagIds` / `privateTagIds` are what lands; `onRemove(entry)` takes one off;
 * `note` is utils/itemTags.js launchTagsNote's sentence. `activeTagId`: the
 * person's ACTIVE TAG among the private ones, marked "Your active tag"
 * (docs/plans/tags-active-tag-proposal.md — every step of a multi add).
 */
export default function LaunchTagsRow({
	collectionId,
	tagIds = [],
	privateTagIds = [],
	note = null,
	onRemove,
	onDismissNote,
	label = 'Tags',
	testId,
	activeTagId = null,
}) {
	const wanted = tagIds.length > 0;
	const { tags: vocabulary } = useCollectionTags(collectionId || null, { enabled: Boolean(collectionId) && wanted });
	const privateTags = useOptionalTags()?.tags || null;
	const entries = useMemo(() => itemTagEntries(
		{ tagIds, myTags: privateTagIds.map(tagId => ({ tagId, kind: 'tag' })) },
		{ vocabulary, privateTags, titles: rememberedItemTagTitles(), activeTagId }
	), [tagIds, privateTagIds, vocabulary, privateTags, activeTagId]);

	if (!entries.length && !note) return null;
	return (
		<div className="launch-tags" data-testid={testId}>
			<div className="micro-label launch-tags__kicker">{label}</div>
			{note ? (
				<Alert type="info" showIcon closable className="launch-tags__note" message={note} onClose={onDismissNote} />
			) : null}
			<ItemTagChips entries={entries} onRemove={onRemove} canRemove={() => typeof onRemove === 'function'} label={label} />
		</div>
	);
}
